import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import type { PipelineStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import type { GridColumnVisibilityModel } from '@/ui/primitives';

export type PipelineTableStatus = PipelineStatusKey;
export type PipelineSortDirection = 'asc' | 'desc';
export type PipelineSortKey =
  | 'name'
  | 'latestRunTimestamp'
  | 'metrics.passRate'
  | 'metrics.passRateDelta'
  | 'metrics.frequencyPerDay'
  | 'metrics.frequencyPerDayDelta'
  | 'metrics.flakeScore'
  | 'metrics.flakeScoreDelta'
  | 'metrics.averageDurationMs'
  | 'metrics.averageDurationMsDelta';

export const pipelineDefaultSortKey: PipelineSortKey = 'latestRunTimestamp';
export const pipelineDefaultSortDirection: PipelineSortDirection = 'desc';

export const pipelineSortOptions: Array<{ key: PipelineSortKey; label: string }> = [
  { key: 'name', label: getDetailLabel('pipeline') },
  { key: 'latestRunTimestamp', label: 'Latest' },
  { key: 'metrics.passRate', label: getChartTitle('passRate') },
  { key: 'metrics.passRateDelta', label: 'Pass Rate Delta' },
  { key: 'metrics.frequencyPerDay', label: getChartTitle('frequency') },
  { key: 'metrics.frequencyPerDayDelta', label: 'Frequency Delta' },
  { key: 'metrics.averageDurationMs', label: getChartTitle('avgDuration') },
  { key: 'metrics.averageDurationMsDelta', label: 'Avg. Duration Delta' },
  { key: 'metrics.flakeScore', label: getChartTitle('flakeScore') },
  { key: 'metrics.flakeScoreDelta', label: 'Flake Score Delta' },
];

export const pipelineQuickSortOptions: Array<{
  key: PipelineSortKey;
  label: string;
  metric: 'passRate' | 'frequency' | 'avgDuration' | 'flakeScore';
  firstDirection?: 'asc' | 'desc';
}> = [
  { key: 'metrics.passRate', label: getChartTitle('passRate'), metric: 'passRate' },
  { key: 'metrics.frequencyPerDay', label: getChartTitle('frequency'), metric: 'frequency' },
  { key: 'metrics.averageDurationMs', label: getChartTitle('avgDuration'), metric: 'avgDuration' },
  {
    key: 'metrics.flakeScore',
    label: getChartTitle('flakeScore'),
    metric: 'flakeScore',
    firstDirection: 'asc',
  },
];

export const pipelineSortFieldMap: Record<PipelineSortKey, string> = {
  name: 'name',
  latestRunTimestamp: 'latestRunTimestamp',
  'metrics.passRate': 'executionPassRate',
  'metrics.passRateDelta': 'executionPassRateDelta',
  'metrics.frequencyPerDay': 'executionFrequencyPerDay',
  'metrics.frequencyPerDayDelta': 'executionFrequencyPerDayDelta',
  'metrics.averageDurationMs': 'executionAverageDurationMs',
  'metrics.averageDurationMsDelta': 'executionAverageDurationMsDelta',
  'metrics.flakeScore': 'executionFlakeScore',
  'metrics.flakeScoreDelta': 'executionFlakeScoreDelta',
};

export const pipelineColumnOptions: Array<{ field: string; label: string; mandatory?: boolean }> = [
  { field: 'name', label: getDetailLabel('pipeline'), mandatory: true },
  { field: 'latestRunStatus', label: getChartTitle('latestStatus') },
  { field: 'stageStatusCounts', label: 'Latest Stages' },
  { field: 'latestJobStatusCounts', label: 'Latest Jobs' },
  { field: 'buildStatusCounts', label: 'Runs' },
  { field: 'metricsSummary', label: getDetailLabel('metrics') },
  { field: 'actions', label: getDetailLabel('actions') },
];

export const pipelineStatusOptions: Array<{ value: PipelineTableStatus; label: string }> = [
  { value: 'success', label: 'Completed' },
  { value: 'warning', label: 'Warning' },
  { value: 'failed', label: 'Failed' },
  { value: 'running', label: 'Running' },
  { value: 'queued', label: 'Queued' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'action_required', label: 'Action Required' },
  { value: 'timed_out', label: 'Timed Out' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'none', label: 'No Runs' },
];

export const pipelineDefaultColumnVisibilityModel: GridColumnVisibilityModel = {
  name: true,
  latestRunStatus: true,
  stageStatusCounts: true,
  latestJobStatusCounts: true,
  buildStatusCounts: true,
  metricsSummary: true,
  actions: true,
};
